import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { tap } from 'rxjs/operators';
import { EnumValidValueModel } from '../models/enum/enum-valid-value.model';
import { EnumValidValueItemModel } from '../models/enum/enum-valid-value-item.model';
import { FormItemActionStepConfigurationModel } from '../models/forms/form-item-action-step-configuration.model';
import { FormParagraphDataModel } from '../models/forms/data/form-paragraph-data.model';
import { Globals } from '../globals';
import { OperatoryDataModel } from '../models/clients/operatory-data.model';
import { OperatoriesService } from '../modules/configuration/operatories/operatories.service';
import { DoctorsService } from './doctors.service';
import { DoctorDataModel } from '../models/doctors/doctor-data.model';
import { DoctorQueryOptionsModel } from '../models/doctors/doctor-query-options.model';
import { FormItemActionConfigurationModel } from '../models/forms/form-item-action-configuration.model';
import { EnterpriseService } from './enterprise.service';
import { TdoEnterpriseConfigurationModel } from '../models/configuration/tdo-enterprise-configuration.model';
import { LocalStorageService } from './local-storage.service';
import { OrganizationsService } from '../modules/configuration/organization-configuration/organizations-service';
import { OrganizationDataModel } from '../models/organization/organization-data.model';

@Injectable({
    providedIn: 'root'
})

export class StaticDataService {
    isReady = false;
    validValuesReadyEvent: Subject<any> = new Subject<any>();

    private validValueArray: EnumValidValueModel[];
    private validValueItemsArray: EnumValidValueItemModel[];
    private itemActionsArray: FormItemActionConfigurationModel[];
    private itemActionStepsArray: FormItemActionStepConfigurationModel[];

    currentLocationOperatories: OperatoryDataModel[];
    currentLocationDoctors: DoctorDataModel[];
    enterprise: TdoEnterpriseConfigurationModel;
    enterpriseOrganization: OrganizationDataModel;
    defaultLanguageEnum: EnumValidValueItemModel;

    constructor(private readonly http: HttpClient,
                private readonly enterpriseService: EnterpriseService,
                private readonly organizationsService: OrganizationsService,
                private readonly localStorageService: LocalStorageService,
                private readonly globals: Globals,
                private readonly operatoryService: OperatoriesService,
                private readonly doctorsService: DoctorsService) {

    }

    refreshingValues: boolean = false;
    refreshingValidValues: boolean = false;
    refreshingValidValueItems: boolean = false;

    async refreshValues(reloadValidValues: boolean = true, reloadDoctors: boolean = true, reloadActions: boolean = true, reloadEnterprise: boolean = true) {
        if (!this.refreshingValues) {
            this.isReady = false;
            this.refreshingValues = true;
            const promises: any[] = [];

            if (reloadValidValues) {
                if (!this.refreshingValidValues)
                {promises.push(this.getAllValidValues().toPromise()); }
                if (!this.refreshingValidValueItems)
                {promises.push(this.getAllValidValueItems().toPromise()); }

            }
            if (reloadDoctors) {
                this.getCurrentLocationDoctors();
                this.getCurrentLocationOperatories();
            }
            if (reloadActions) {
                promises.push(this.getAllItemActions().toPromise());
                promises.push(this.getAllItemActionSteps().toPromise());
            }
            if (reloadEnterprise) {
                this.getEnterprise();
            }
            try {
                const results: any[] = await Promise.all(promises);
                this.refreshingValues = false;
            } catch (err) {
                console.log('Error reloading values.', err);
            }
        }

    }

    getAllValidValues(): Observable<EnumValidValueModel[]> {
        console.log('getAllValidValues');
        if (!this.refreshingValidValues) {
            this.refreshingValidValues = true;
            this.isReady = false;
            console.log('Try Loading Valid value items ....');
            const method: string = 'valid-values-v2/get-valid-values';
            const url = environment.TDOurl + method;
            return this.http.get<EnumValidValueModel[]>(url, UserService.authorizationHeaderObject)
                .pipe(
                    tap((_) => {
                        this.validValueArray = _;
                        this.refreshingValidValues = false;
                        if (this.validValueArray && this.validValueItemsArray) {
                            this.isReady = true;
                            this.validValuesReadyEvent.complete();
                            console.log('Valid value items are ready');
                        }                        
                        else {
                            this.getAllValidValueItems().subscribe({
                                complete: () => {}
                            });
                        }
                    })
                );
        }
        else
            return of(this.validValueArray);
    }

    getAllValidValueItems(): Observable<EnumValidValueItemModel[]> {
        if (!this.refreshingValidValueItems) {
            this.refreshingValidValueItems = true;
            const method: string = 'valid-values-v2/get-valid-value-items';
            const url = environment.TDOurl + method;
            return this.http.get<EnumValidValueItemModel[]>(url, UserService.authorizationHeaderObject)
                .pipe(
                    tap((_) => {
                        this.validValueItemsArray = _;
                        this.refreshingValidValueItems = false;

                        if (this.validValueArray && this.validValueItemsArray) {
                            this.isReady = true;
                            this.validValuesReadyEvent.complete();
                        }
                        else {
                            this.getAllValidValues().subscribe({
                                complete: () => {}
                            });
                        }
                    })
                );
        }
        else
        return of(this.validValueItemsArray);
    }

    getAllItemActions(): Observable<FormItemActionConfigurationModel[]> {
        const method: string = 'forms/get-item-actions';
        const url = environment.TDOurl + method;
        return this.http.get<FormItemActionConfigurationModel[]>(url, UserService.authorizationHeaderObject)
            .pipe(
                tap((_) => {
                    this.itemActionsArray = _;
                })
            );
    }

    getAllItemActionSteps(): Observable<FormItemActionStepConfigurationModel[]> {
        const method: string = 'forms/get-item-action-steps';
        const url = environment.TDOurl + method;
        return this.http.get<FormItemActionStepConfigurationModel[]>(url, UserService.authorizationHeaderObject)
            .pipe(
                tap((_) => {
                    this.itemActionStepsArray = _;
                })
            );
    }

    get validValues() {
        return this.validValueArray;
    }

    get validValueItems() {
        return this.validValueItemsArray;
    }

    get itemActionSteps() {
        return this.itemActionStepsArray;
    }

    get itemActions() {
        return this.itemActionsArray;
    }

    getValidValueItemsByItemIds(idsArray: string[]): EnumValidValueItemModel[] {
        if (this.validValueItems) {
            const retArray = new Array<EnumValidValueItemModel>();
            for (const str of idsArray) {
                if (str === null || str === undefined) {
                }
                else {
                    const item = this.validValueItems.find(p => p.ValidValueItemId.toLowerCase() === str.toLowerCase());
                    retArray.push(item);
                }
            }

            return retArray;
        } else {
            return null;
        }
    }

    getValidValueItemByItemId(validValueItemId: string): EnumValidValueItemModel {
        return this.validValueItems.find(p => p.ValidValueItemId === validValueItemId);
    }

    getValidValueItemsByValidValueId(validValueId: string): EnumValidValueItemModel[] {
        if (this.validValueItems) {
            // the ValidValueID returned from DB is lowercase, so for now converting the validvalueid string param to lowercase as well
            const retArray = this.validValueItems.filter(p => p.ValidValueId === validValueId.toLowerCase());

            return retArray;
        } else {
            return null;
        }
    }

    getValidValueItemsByValidValueName(validValueName: string): EnumValidValueItemModel[] {
        if (this.validValueItems) {
            // if (this.validValueItems && this.validValueArray) {
            // the ValidValueID returned from DB is lowercase, so for now converting the validvalueid string param to lowercase as well
            const validValue = this.validValueArray.find(p => p.ValidValueName === validValueName);
            if (validValue) {
                let items = this.validValueItems.filter(p => p.ValidValueId === validValue.ValidValueId);
                items = items.sort((a, b) => a.ItemOrder - b.ItemOrder);
                return items;
            }
        } 
        console.log('Valid Value Items are not ready yet: validValueItemsArray');
        return null;        
    }

    updateValidValueItems(enums: EnumValidValueItemModel[]) {
        for (const item of enums) {
            let orig = this.validValueItemsArray.find(p => p.ValidValueItemId === item.ValidValueItemId);
            if (orig) {
                orig = item;
            }
        }
    }

    // Added this because including this in the backend query was doubling the amount of time it takes to process as well as
    // doubling the size of the response.  Currently adds enumValidValueItems and cfgItemActionSteps
    addStaticFormData(dataFormParagraph: FormParagraphDataModel) {
        for (const item of dataFormParagraph.dataFormItems) {
            // Insert valid value items
            if (item.cfgDFormItem.ValidValue) {
                const valueId = item.cfgDFormItem.ValidValueId;
                const validItems = this.validValueItems.filter(p => p.ValidValueId === valueId);
                if (validItems) {
                    item.cfgDFormItem.ValidValue.enumValidValueItems = validItems;
                }
            }

            // Insert item action steps
            if (item.cfgDFormItem && item.cfgDFormItem.AfterUpdate_ItemActionId) {
                const actionId = item.cfgDFormItem.AfterUpdate_ItemActionId;
                const actions = this.itemActions.find(p => p.ItemActionId.toLowerCase() === actionId.toLowerCase());
                if (actions) {
                    item.cfgDFormItem.AfterUpdateItemAction = actions;
                }
            }
        }
    }

    getCurrentLocationOperatories() {
        this.operatoryService.getLocationOperatories(this.globals.LOCID).subscribe((data) => {
            this.currentLocationOperatories = data;
        });
    }

    // TODO: Why is this here instead of in DoctorService?
    getCurrentLocationDoctors() {
        const queryModel = new DoctorQueryOptionsModel();
        queryModel.isLazyLoad = false;
        this.doctorsService.getDoctors(queryModel).subscribe((data) => {
            // this.currentLocationDoctors = data.filter(p => p.LOCID.toString() === this.globals.LOCID);
            // TODO: Make sure the doctor works at the current location
            this.currentLocationDoctors = data;
        });
    }

    saveValidValueItem(enu: EnumValidValueItemModel, isNew: boolean = false) {
        return this.http.post<EnumValidValueItemModel>('api/valid-value/save-valid-value-item', enu)
            .pipe(
                tap(_ => {
                    let exists = -1;
                    if (!isNew) {
                        exists = this.validValueItems.findIndex(p => p.ValidValueItemId.toLowerCase() === enu.ValidValueItemId.toLowerCase());
                    }
                    if (exists > -1) {
                        this.validValueItems[exists] = enu;
                    } else {
                        enu.ValidValueItemId = _.ValidValueItemId;
                        this.validValueItems.push(enu);
                    }
                })
            );
    }

    reorderValidValueItems(items: EnumValidValueItemModel[]) {
        return this.http.post<EnumValidValueItemModel[]>('api/valid-values/reorder', items);
    }

    getEnterprise() {
        if (this.isReady) {
            this.enterpriseService.getEnterpriseById(this.localStorageService.enterpriseId).subscribe((ent) => {
                if (ent) {
                    this.enterprise = ent;
                    this.getOrganization(ent.OrganizationID);
                }
            });
        } else {
            this.validValuesReadyEvent.subscribe({
                complete: () => {
                    this.enterpriseService.getEnterpriseById(this.localStorageService.enterpriseId).subscribe((ent) => {
                        if (ent) {
                            this.enterprise = ent;
                            this.getOrganization(ent.OrganizationID);
                        }
                    });
                }
            });
        }
    }

    getOrganization(organizationId) {
        this.organizationsService.getOrganization(organizationId).subscribe((res) => {
            this.enterpriseOrganization = res;
            this.defaultLanguageEnum = this.validValueItems.find(p => p.ValidValueItemId.toLowerCase() === res.dataAddress?.enumCountryID);
        });
    }

    getValidValueItemsByName(validValueName: string): Observable<EnumValidValueItemModel[]> {

        const method: string = 'valid-values-v2/getValidValuesItems-by-validValueName/' + validValueName;
        const url = environment.TDOurl + method;
        return this.http.get<EnumValidValueItemModel[]>(url, UserService.authorizationHeaderObject);

    }


    getEnumValidValueItemsByValidValueName(ValidValueName: string): Observable<EnumValidValueItemModel[]> {
        const result = this.getValidValueItemsByValidValueName(ValidValueName);
         return result? of(result): this.getValidValueItemsByName(ValidValueName);
      }

}
